import { API_URL, STK } from "../config/apiConfig";
import { useContext } from "react";
import { LoggerContext } from "../context/LoggerContext";
import { useErrorBoundary } from "../context/ErrorBoundaryContext";

/**
 * Extracts error message from API response
 * @param {Object} apiResponse - API response object
 * @returns {string} - Extracted error message
 */
export const extractErrorMessage = (apiResponse) => {
    if (!apiResponse) return "Unknown API error";
    
    // Check for message in multiple common locations
    if (apiResponse.message) return apiResponse.message;
    if (apiResponse.status?.message) return apiResponse.status.message;
    if (apiResponse.error_message) return apiResponse.error_message;
    if (apiResponse.errorDetails?.message) return apiResponse.errorDetails.message;
    if (apiResponse.statusText) return `${apiResponse.statusText} (${apiResponse.httpStatus || apiResponse.status})`;
    
    // Fall back to generic message with status code if possible
    if (apiResponse.httpStatus || apiResponse.status) {
        return `Error ${apiResponse.httpStatus || apiResponse.status}: Request failed`;
    }
    
    return "API request failed with unknown error";
};

/**
 * Broadcasts an API error to the application
 * This allows central error handling through the ErrorBoundaryContext
 * 
 * @param {Object} error - Error object or response
 * @param {Object} context - Additional context for the error
 */
export const broadcastApiError = (error, context = {}) => {
    const errorEvent = new CustomEvent('api-error', {
        detail: {
            error: {
                ...error,
                message: extractErrorMessage(error)
            },
            source: context.endpoint || 'api-request',
            context
        }
    });
    
    window.dispatchEvent(errorEvent);
};

/**
 * Custom hook for API requests with error handling and logging
 * @returns {Object} - API request functions with context integration
 */
export const useApiWithLogger = () => {
    const logger = useContext(LoggerContext);
    const errorBoundary = useErrorBoundary();
    
    /**
     * API request with logger and error handling context
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Request options
     * @returns {Promise<Object>} - API response
     */
    const apiRequestWithContext = async (endpoint, options = {}) => {
        try {
            const response = await apiRequest(endpoint, options, logger);
            
            // If response indicates an error, handle it through the error boundary
            if (!response.success && errorBoundary?.addError && !options.suppressErrorNotification) {
                errorBoundary.addError(response, {
                    source: `api/${endpoint}`,
                    variant: 'error',
                    dismissible: true,
                });
            }
            
            return response;
        } catch (error) {
            // Handle unexpected errors
            if (errorBoundary?.addError && !options.suppressErrorNotification) {
                errorBoundary.addError(error, {
                    source: `api/${endpoint}`,
                    variant: 'error',
                    dismissible: true,
                });
            }
            
            throw error;
        }
    };
    
    return {
        apiRequest: apiRequestWithContext,
        fetchApi: async (suffix, otk = null, raw = false, options = {}) => {
            return apiRequestWithContext(suffix, { otk, raw, ...options });
        },
        submitFormData: async (suffix, formData, otk = null, options = {}) => {
            return apiRequestWithContext(suffix, {
                method: 'POST',
                data: formData,
                otk,
                raw: true,
                ...options
            });
        },
        uploadFiles: async (suffix, files, options = {}) => {
            const formData = new FormData();
            
            // Add reference ID and type if provided
            if (options.refId !== null && options.refId !== undefined) {
                formData.append('ref_id', options.refId.toString());
            }
            
            if (options.refType !== null && options.refType !== undefined) {
                formData.append('ref_type', options.refType.toString());
            }
            
            if (Array.isArray(files)) {
                files.forEach((file, index) => {
                    formData.append(`file[${index}]`, file);
                });
            } else {
                formData.append('file[0]', files);
            }
            
            // Add any metadata as additional fields
            if (options.metadata) {
                Object.entries(options.metadata).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
                    }
                });
            }
            
            const uploadEndpoint = suffix.startsWith('/') ? suffix.substring(1) : suffix;
            
            return apiRequestWithContext(uploadEndpoint, {
                method: 'POST',
                data: formData,
                otk: options.otk,
                raw: true,
                debug: options.debug,
                onUploadProgress: options.onProgress,
                signal: options.signal
            });
        },
        updateCartItemQuantity: async (cartToken, item, quantity, otk, options = {}) => {
            return apiRequestWithContext(`checkout/${cartToken}/item/${item.id}`, {
                method: 'PUT',
                data: {
                    count: quantity,
                    type_variant_id: 0,
                    instance: {
                        id: item.id,
                        title: item.product_name,
                        desc: item.description || "",
                        cost: {
                            sale_price: item.price
                        }
                    }
                },
                otk,
                raw: true,
                debug: true,
                ...options
            });
        }
    };
};

/**
 * Centralized error handler for API requests
 * @param {Error} error - Error object
 * @param {Object} context - Error context
 * @param {Object} logger - Logger context
 */
const handleApiError = (error, context, logger) => {
    const errorInfo = {
        message: error.message || "Unknown API error",
        stack: error.stack,
        ...context
    };
    
    // Log to console in any case for immediate debugging
    console.error("API Request Error:", errorInfo);
    
    // Use logger context if available
    if (logger?.logError) {
        logger.logError(error, context);
    }
    
    // Broadcast the error for central handling
    broadcastApiError(errorInfo, context);
    
    // Return standardized error response
    return {
        success: false,
        error: true,
        message: errorInfo.message,
        httpStatus: context.httpStatus || 500,
        errorDetails: errorInfo
    };
};

/**
 * Normalizes endpoint to avoid double slashes in URL construction
 * @param {string} endpoint - The API endpoint
 * @returns {string} - Normalized endpoint without leading slash
 */
const normalizeEndpoint = (endpoint) => {
	return endpoint?.startsWith('/') ? endpoint.substring(1) : endpoint;
};

/**
 * Generic API request function with consistent error handling
 * 
 * @param {string} endpoint - API endpoint to call (without base URL)
 * @param {Object} options - Request options
 * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} options.data - Data to send in the request body
 * @param {string} options.otk - Organization token for authentication
 * @param {boolean} options.raw - If true, returns the raw response without extracting data
 * @param {boolean} options.debug - If true, logs debug info about the request
 * @param {boolean} options.suppressErrorNotification - If true, doesn't broadcast errors
 * @param {Object} logger - Logger context (optional)
 * @returns {Promise<Object>} - API response with standardized format
 */
export const apiRequest = async (endpoint, options = {}, logger = null) => {
	// Normalize endpoint to prevent double slashes
	const normalizedEndpoint = normalizeEndpoint(endpoint);
	const requestId = `api-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
	const context = { endpoint: normalizedEndpoint, requestId, options: { ...options } };
    
    try {
        const {
            method = 'GET',
            data = null,
            otk = null,
            debug = false,
            suppressErrorNotification = false,
            onUploadProgress = null
        } = options;

        // Enhanced debugging
        if (debug) {
            const debugInfo = {
                providedOtk: otk,
                type: typeof otk,
                endpoint: normalizedEndpoint,
                method,
                requestId
            };
            
            if (logger?.logDebug) {
                logger.logDebug("APIRequest Debug - OTK check:", debugInfo);
            } else {
                console.log("APIRequest Debug - OTK check:", debugInfo);
            }
        }

        // Set up headers with more robust OTK handling
        const headers = {
            "STK": STK
        };
        
        if (otk) {
            // Ensure OTK is properly formatted - remove any whitespace
            headers["OTK"] = otk.trim();
            
            if (debug) {
                if (logger?.logDebug) {
                    logger.logDebug(`[${requestId}] Setting OTK header:`, headers["OTK"]);
                } else {
                    console.log(`[${requestId}] Setting OTK header:`, headers["OTK"]);
                }
            }
        }

        // Debug all headers
        if (debug) {
            if (logger?.logDebug) {
                logger.logDebug(`[${requestId}] Final request headers:`, headers);
            } else {
                console.log(`[${requestId}] Final request headers:`, headers);
            }
        }
        
        const requestOptions = {
            method,
            headers
        };
        
        // Special handling for FormData vs JSON data
        if (data && method !== 'GET') {
            if (data instanceof FormData) {
                // For FormData, don't set Content-Type header - browser will set it automatically with boundary
                requestOptions.body = data;
                
                // If we have an upload progress handler and XMLHttpRequest is available, use it
                if (onUploadProgress && typeof XMLHttpRequest !== 'undefined') {
                    return new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        xhr.open(method, `${API_URL}/${normalizedEndpoint}`);
                        
                        // Add headers
                        Object.keys(headers).forEach(key => {
                            xhr.setRequestHeader(key, headers[key]);
                        });
                        
                        // Set up progress tracking
                        xhr.upload.addEventListener('progress', onUploadProgress);
                        
                        // Set up completion handlers
                        xhr.onload = async () => {
                            let responseData;
                            try {
                                responseData = JSON.parse(xhr.responseText);
                            } catch (e) {
                                responseData = { message: 'Invalid JSON response' };
                            }
                            
                            const standardizedResponse = {
                                ...responseData,
                                httpStatus: xhr.status,
                                success: xhr.status >= 200 && xhr.status < 300,
                                requestId
                            };
                            
                            if (!standardizedResponse.success) {
                                standardizedResponse.error = true;
                                standardizedResponse.message = responseData?.status?.message || `API error: ${xhr.statusText}`;
                                
                                if (!suppressErrorNotification) {
                                    broadcastApiError(standardizedResponse, {
                                        endpoint: normalizedEndpoint,
                                        requestId,
                                        httpStatus: xhr.status
                                    });
                                }
                            }
                            
                            resolve(standardizedResponse);
                        };
                        
                        xhr.onerror = () => {
                            reject(new Error('Network error occurred'));
                        };
                        
                        xhr.onabort = () => {
                            reject(new Error('Upload aborted'));
                        };
                        
                        // Add abort signal if provided
                        if (options.signal) {
                            options.signal.addEventListener('abort', () => {
                                xhr.abort();
                            });
                        }
                        
                        // Send the FormData
                        xhr.send(data);
                    });
                }
            } else {
                // For JSON data, set Content-Type header and stringify the data
                headers["Content-Type"] = "application/json";
                requestOptions.body = JSON.stringify(data);
            }
        }
        
        // Log complete request details
        if (debug) {
            const requestDetails = {
                url: `${API_URL}/${normalizedEndpoint}`,
                options: { 
                    ...requestOptions,
                    body: data instanceof FormData 
                        ? "FormData object - contents not serializable for logging"
                        : requestOptions.body
                },
                headers: Object.fromEntries(Object.entries(headers))
            };
            
            if (logger?.logDebug) {
                logger.logDebug(`[${requestId}] Complete request details:`, requestDetails);
            } else {
                console.log(`[${requestId}] Complete request details:`, requestDetails);
            }
        }

        const response = await fetch(`${API_URL}/${normalizedEndpoint}`, requestOptions);
        
        // Enhanced error context
        context.httpStatus = response.status;
        context.statusText = response.statusText;
        
        // Enhanced error logging for non-200 responses
        if (!response.ok) {
            const errorContext = {
                status: response.status,
                statusText: response.statusText,
                url: `${API_URL}/${normalizedEndpoint}`,
                headers: Object.fromEntries(response.headers.entries()),
                requestId
            };
            
            if (logger?.logDebug) {
                logger.logDebug(`[${requestId}] API Request failed:`, errorContext);
            } else {
                console.error(`[${requestId}] API Request failed:`, errorContext);
            }
        }

        let responseData;
        try {
            responseData = await response.json();
        } catch (jsonError) {
            throw new Error(`Failed to parse API response as JSON: ${jsonError.message}`);
        }
        
        // Debug response
        if (debug) {
            const responseDetails = {
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries()),
                data: responseData,
                requestId
            };
            
            if (logger?.logDebug) {
                logger.logDebug(`[${requestId}] API Response:`, responseDetails);
            } else {
                console.log(`[${requestId}] API Response:`, responseDetails);
            }
        }
        
        // Return standardized response with success flag based on HTTP status
        const standardizedResponse = {
            ...responseData,
            httpStatus: response.status,
            success: response.status >= 200 && response.status < 300,
            requestId
        };

        // If response is not successful, add error flag and message
        if (!standardizedResponse.success) {
            standardizedResponse.error = true;
            console.log("response", responseData);
            standardizedResponse.message = responseData?.status?.message || `API error: ${response.statusText}`;
            
            // Log error using logger context if available
            if (logger?.logError) {
                logger.logError(
                    new Error(standardizedResponse.message), 
                    { 
                        endpoint: normalizedEndpoint, 
                        httpStatus: response.status, 
                        response: responseData,
                        requestId
                    }
                );
            }
            
            // Broadcast the error for central handling if not suppressed
            if (!suppressErrorNotification) {
                broadcastApiError(standardizedResponse, {
                    endpoint: normalizedEndpoint,
                    requestId,
                    httpStatus: response.status
                });
            }
        }
        
        return standardizedResponse;
        
    } catch (error) {
        return handleApiError(error, context, logger);
    }
};

// Helper to extract endpoint from full URL
export const extractEndpoint = (url) => {
    if (url.includes(API_URL)) {
        return url.replace(`${API_URL}/`, '');
    }
    return url;
};

// New function for Organization
export const fetchOrganization = async (orgUrl) => {
    return apiRequest(`organizations/public/${orgUrl}`);
};

// Existing fetchApi with OTK parameter
export const fetchApi = async (suffix, otk = null, raw = false, options = {}) => {
    return apiRequest(suffix, { otk, raw, ...options });
};

/**
 * Submit form data to the API
 * 
 * @param {string} suffix - API endpoint suffix
 * @param {Object} formData - Data to send in the request
 * @param {string} otk - Organization token for authentication
 * @param {Object} options - Additional options for the request
 * @returns {Promise<Object>} - API response
 */
export const submitFormData = async (suffix, formData, otk = null, options = {}) => {
    return apiRequest(suffix, {
        method: 'POST',
        data: formData,
        otk,
        raw: true,
        ...options
    });
};

// For backward compatibility with existing code
export const submitMultipartFormData = async (url, data, options = {}) => {
    const endpoint = extractEndpoint(url);
    
    return apiRequest(endpoint, {
        method: options.method || 'PUT',
        data,
        otk: options.otk,
        raw: true,
        debug: options.debug,
        suppressErrorNotification: options.suppressErrorNotification
    });
};

/**
 * Updates cart item quantity
 * @param {string} cartToken - The cart token
 * @param {object} item - The item to update
 * @param {number} quantity - New quantity
 * @param {string} otk - Organization token
 * @param {Object} options - Additional options for the request
 */
export const updateCartItemQuantity = async (cartToken, item, quantity, otk, options = {}) => {
    return apiRequest(`checkout/${cartToken}/item/${item.id}`, {
        method: 'PUT',
        data: {
            count: quantity,
            type_variant_id: 0,
            instance: {
                id: item.id,
                title: item.product_name,
                desc: item.description || "",
                cost: {
                    sale_price: item.price
                }
            }
        },
        otk,
        raw: true,
        debug: true,
        ...options
    });
};

/**
 * Uploads one or multiple files to the server
 * 
 * @param {string} endpoint - API endpoint to upload to (e.g. 'uploads/public')
 * @param {File|File[]} files - Single file or array of files to upload
 * @param {Object} options - Upload configuration options
 * @param {string} options.otk - Organization token (optional)
 * @param {Function} options.onProgress - Progress callback function (event) => void
 * @param {AbortSignal} options.signal - AbortController signal to cancel upload
 * @param {Object} options.metadata - Additional metadata to include with the upload
 * @param {number|string} options.refId - Reference ID for file association
 * @param {number|string} options.refType - Reference type for file association
 * @param {boolean} options.debug - Enable debug logging
 * @returns {Promise<Object>} - Server response with file URLs
 */
export const uploadFiles = async (endpoint, files, options = {}) => {
    const {
        otk = null,
        onProgress = null,
        signal = null,
        metadata = {},
        refId = null,
        refType = null,
        debug = false
    } = options;

    // Create FormData instance
    const formData = new FormData();
    
    // Add reference ID and type if provided
    if (refId !== null && refId !== undefined) {
        formData.append('ref_id', refId.toString());
    }
    
    if (refType !== null && refType !== undefined) {
        formData.append('ref_type', refType.toString());
    }
    
    // Handle single file or multiple files
    if (Array.isArray(files)) {
        // Multiple files
        files.forEach((file, index) => {
            formData.append(`file[${index}]`, file);
        });
    } else {
        // Single file
        formData.append('file[0]', files);
    }
    
    // Add any metadata as additional fields
    Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
        }
    });
    
    // Ensure endpoint is properly formatted
    const uploadEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    
    // Make the API request with progress tracking
    return apiRequest(uploadEndpoint, {
        method: 'POST',
        data: formData,
        otk,
        raw: true,
        debug,
        onUploadProgress: onProgress,
        signal
    });
};